import { Calendar, Pencil, User } from "lucide-react";
import { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import FormHeader from "../../../components/form/FormHeader";
import { formatDateCustom } from "../../../utils/string.utlis";
import { plainTextToHtml } from "../../../lib/utils";
import { isHtmlContent } from "../utils/news.utils";
import { useGetNewsDetail } from "../hooks/useGetNewsDetail";
import type { NewsDetailResponse, NewsListItemResponse } from "../types/news.types";

type NewsLocationState = {
  news?: NewsDetailResponse | NewsListItemResponse;
};

const isNewsDetail = (
  value: NewsDetailResponse | NewsListItemResponse,
): value is NewsDetailResponse => "created_at" in value;

function ViewNewsArticlePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const rawInitialNews = (location.state as NewsLocationState | null | undefined)?.news ?? null;
  const initialNews = rawInitialNews && isNewsDetail(rawInitialNews) ? rawInitialNews : null;
  const { newsDetail, loading, error, refetch } = useGetNewsDetail(id, initialNews);

  const contentHtml = useMemo(() => {
    if (!newsDetail?.content) return "";
    return isHtmlContent(newsDetail.content) ? newsDetail.content : plainTextToHtml(newsDetail.content);
  }, [newsDetail?.content]);

  const handleBack = () => {
    navigate("/news");
  };

  if (loading && !newsDetail) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
          <p className="text-sm text-muted-foreground">{t("news.loading_news")}</p>
        </div>
      </div>
    );
  }

  if (error && !newsDetail) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 dark:bg-gray-900">
        <div className="max-w-md text-center">
          <p className="mb-4 text-sm text-red-600 dark:text-red-400">{error}</p>
          <div className="flex items-center justify-center gap-3">
            <Button variant="outline" onClick={() => void refetch()}>
              {t("ui.retry")}
            </Button>
            <Button onClick={handleBack}>{t("news.center")}</Button>
          </div>
        </div>
      </div>
    );
  }

  if (!newsDetail) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <FormHeader
        onBack={handleBack}
        title={newsDetail.title}
        subtitle=""
        backLabel={t("common.back")}
        actions={
          <Button
            onClick={() => navigate(`/news/update/${id ?? ""}`)}
            className="flex items-center gap-2"
          >
            <Pencil className="h-4 w-4" />
            {t("common.edit")}
          </Button>
        }
      />
      <div className="p-6">
        <div className="mx-auto max-w-5xl">
          <Card className="overflow-hidden border-0 bg-white shadow-sm dark:bg-gray-800">
          <div className="bg-white px-6 py-8 dark:bg-gray-800">
            <div className="mb-8 border-b border-gray-100 pb-8 dark:border-gray-700">
              <h1 className="break-words text-2xl font-bold leading-tight text-gray-900 dark:text-white md:text-4xl">
                {newsDetail.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
                {newsDetail.creator_name && (
                  <div className="flex min-w-0 items-center gap-2">
                    <User className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="truncate">{newsDetail.creator_name}</span>
                  </div>
                )}

                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 shrink-0" aria-hidden="true" />
                  <time dateTime={newsDetail.updated_at}>
                    {formatDateCustom(newsDetail.updated_at, "MMM, DD, YYYY")}
                  </time>
                </div>
              </div>
            </div>

            {newsDetail.thumbnail_url && (
              <div className="mb-8 overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700">
                <img
                  src={newsDetail.thumbnail_url}
                  alt={newsDetail.title}
                  className="h-full w-full object-cover"
                  style={{ maxHeight: "520px" }}
                />
              </div>
            )}

            <div className="prose prose-lg max-w-none dark:prose-invert">
              <div
                className="whitespace-pre-wrap text-gray-700 dark:text-gray-300"
                dangerouslySetInnerHTML={{ __html: contentHtml }}
              />
            </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default ViewNewsArticlePage;
